import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";

import axios from "../../utils/axiosInstance";

import { toast } from "react-toastify";
import { TextSlicer } from "../../utils/textSlicerDots";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function DeleteRecipient({
  members = [],
  device,
  groupId,
  onRecipientsUpdated,
}) {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const [stagedForRemoval, setStagedForRemoval] = useState([]);
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [removingUsers, setRemovingUsers] = useState(new Set());
  const [savingRecipients, setSavingRecipients] = useState(false);

  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Current recipients (excluding those staged for removal)
  const currentRecipients = React.useMemo(() => {
    return members.filter(
      (member) => !stagedForRemoval.some((staged) => staged.id === member.id)
    );
  }, [members, stagedForRemoval]);

  // Filtered current recipients based on search
  const filteredCurrentRecipients = React.useMemo(() => {
    if (!currentSearchQuery) return currentRecipients;
    return currentRecipients.filter(
      (user) =>
        (user.name || "")
          .toLowerCase()
          .includes(currentSearchQuery.toLowerCase()) ||
        (user.email || "")
          .toLowerCase()
          .includes(currentSearchQuery.toLowerCase()) ||
        (user.phone || "")
          .toLowerCase()
          .includes(currentSearchQuery.toLowerCase())
    );
  }, [currentRecipients, currentSearchQuery]);

  // Filtered staged for removal based on search
  const filteredStagedForRemoval = React.useMemo(() => {
    if (!selectedSearchQuery) return stagedForRemoval;
    return stagedForRemoval.filter(
      (user) =>
        (user.name || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase()) ||
        (user.email || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase()) ||
        (user.phone || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase())
    );
  }, [stagedForRemoval, selectedSearchQuery]);

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Move user from left to right (stage for deletion)
  const handleStageForRemoval = (user) => {
    setStagedForRemoval((prev) => [...prev, user]);
    toast.success(`${user.name} staged for deletion`, {
      position: "top-right",
    });
  };

  // Move user from right to left (unstage from deletion)
  const handleUnstageFromRemoval = (user) => {
    setStagedForRemoval((prev) => prev.filter((u) => u.id !== user.id));
    toast.success(`${user.name} restored`, {
      position: "top-right",
    });
  };

  const handleSaveChanges = async () => {
    if (stagedForRemoval.length === 0) {
      toast.success("No recipients to delete", {
        position: "top-right",
      });
      return;
    }

    setSavingRecipients(true);
    try {
      const membersByRecipient = stagedForRemoval
        .map((m) => (m?.recipient ? m.id : null))
        .filter(Boolean);

      console.log(
        "🚀 ~ handleSaveChanges ~ membersByRecipient:",
        membersByRecipient
      );

      if (membersByRecipient.length > 0) {
        await axios.delete(
          `${process.env.REACT_APP_API_GATEAWAY}/recipients/device/${device.id}`,
          {
            data: { UI_Description: `${getUIDescription()}`, userIds: membersByRecipient },
          }
        );

        console.log("UI_Description:", getUIDescription());

        toast.success(
          `${stagedForRemoval.length} recipient(s) deleted successfully`,
          {
            position: "top-right",
          }
        );
      }

      setStagedForRemoval([]);

      if (onRecipientsUpdated) {
        onRecipientsUpdated();
      }
    } catch (error) {
      console.error(`Error saving changes:`, error);
      toast.success(`Error deleting recipients`, {
        position: "top-right",
      });
    } finally {
      setSavingRecipients(false);
    }
  };

  const UserListItem = ({ user, showStageButton, showUnstageButton }) => {
    return (
      <ListItem
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          mb: 0.5,
          backgroundColor: "background.paper",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight="bold">
              {user.name}
            </Typography>
          }
        // secondary={
        //   <Typography variant="caption" color="text.secondary">
        //     {isXS ? TextSlicer(user.email, 14) : user.email}
        //   </Typography>
        // }
        />
        <ListItemSecondaryAction>
          {user?.members && (
            <Tooltip
              title="This can't be deleted from here because it has membership. You can delete it from the Notifications tab > notification group details > delete recipients"
              arrow
            >
              <span>
                <IconButton
                  edge="end"
                  size="small"
                  disabled
                  sx={{
                    backgroundColor: "warning.main",
                    color: "white",
                    mr: 1,
                    "&:disabled": {
                      backgroundColor: "warning.main",
                      opacity: 1,
                    },
                  }}
                >
                  <StarIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!showUnstageButton && (
            <IconButton
              edge="end"
              size="small"
              onClick={() => handleStageForRemoval(user)}
              disabled={user?.members ? true : false}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
              title="Stage for deletion"
            >
              {removingUsers.has(user.id) ? (
                <CircularProgress size={16} color="#60a5fa" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </IconButton>
          )}
          {showUnstageButton && (
            <IconButton
              edge="end"
              size="small"
              onClick={() => handleUnstageFromRemoval(user)}
              disabled={removingUsers.has(user.id)}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
              title="Restore recipient"
            >
              {removingUsers.has(user.id) ? (
                <CircularProgress size={16} color="#60a5fa" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Device Info */}
        <Box sx={{ display: "flex", gap: 2, width: "100%", my: 2 }}>
          <TextField
            label="Unit Name *"
            value={device?.name || "No name"}
            disabled
            fullWidth
            size="small"
          />
          <TextField
            label="Modem Serial Number *"
            value={device?.id || "No name"}
            disabled
            fullWidth
            size="small"
          />
        </Box>

        {/* Header */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body2"
            color="error"
            sx={{
              mb: 0.5,
              fontStyle: "italic",
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem",
                md: "0.875rem",
              },
            }}
          >
            Selected Recipients will be deleted from receiving notifications
          </Typography>

          {/* Staged changes info */}
          {stagedForRemoval.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {stagedForRemoval.length} recipient(s) staged for deletion. Click
              "Save Changes" to confirm.
            </Alert>
          )}
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <CircularProgress color="#60a5fa" size={60} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", flex: 1, gap: 2, minHeight: 0 }}>
              {/* Left Panel - Current Recipients */}
              <Box sx={{ width: "50%", height: "56vh" }}>
                <Paper
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "primary.main",
                      color: "black",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      CURRENT RECIPIENTS
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={currentSearchQuery}
                      onChange={(e) => setCurrentSearchQuery(e.target.value)}
                    />
                  </Box>

                  <Box sx={{ flex: 1, overflowY: "auto", px: 0.5, pb: 1.5 }}>
                    <List sx={{ py: 0 }}>
                      {filteredCurrentRecipients.map((user) => (
                        <UserListItem
                          key={user.id}
                          user={user}
                          showStageButton={true}
                          showUnstageButton={false}
                        />
                      ))}
                      {filteredCurrentRecipients.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 4 }}
                        >
                          No current recipients found
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Paper>
              </Box>

              {/* Right Panel - Recipients to Delete */}
              <Box sx={{ width: "50%", height: "56vh" }}>
                <Paper
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "error.main",
                      color: "white",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      RECIPIENTS TO DELETE
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={selectedSearchQuery}
                      onChange={(e) => setSelectedSearchQuery(e.target.value)}
                    />
                  </Box>

                  <Box sx={{ flex: 1, overflowY: "auto", px: 0.5, pb: 1.5 }}>
                    <List sx={{ py: 0 }}>
                      {filteredStagedForRemoval.map((user) => (
                        <UserListItem
                          key={user.id}
                          user={user}
                          showStageButton={false}
                          showUnstageButton={true}
                        />
                      ))}
                      {filteredStagedForRemoval.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 4 }}
                        >
                          No recipients selected for deletion
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={stagedForRemoval.length === 0 || savingRecipients}
                color="error"
              >
                {savingRecipients ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="#60a5fa"
                      sx={{ mr: 1 }}
                    />
                    Deleting...
                  </>
                ) : (
                  `Delete Recipients (${stagedForRemoval.length})`
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
